import { Octree } from '@babylonjs/core/Culling/Octrees/octree';
import { Ray } from '@babylonjs/core/Culling/ray';
import { PointerEventTypes, PointerInfo } from '@babylonjs/core/Events/pointerEvents';
import { Matrix, Plane, Quaternion, Vector3 } from '@babylonjs/core/Maths';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { InstancedMesh } from '@babylonjs/core/Meshes/instancedMesh';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Observer } from '@babylonjs/core/Misc/observable';
import { Node } from '@babylonjs/core/node';
import { Scene } from '@babylonjs/core/scene';
import { Nullable } from '@babylonjs/core/types';
import { eConnectionMode, eConnectorPositionMode, eDragMode, eMateType } from '@kb3d/models/classes/enums';
import { Connector, isConnector } from '@kb3d/models/classes/features/connector';
import { SketchControlPoint } from '@kb3d/models/classes/features/sketch-control-point';
import { IConnection, IDragArgs, IDraggable, IKb3dPointerArgs } from '@kb3d/models/classes/client-enums';
import { PointerInfoMinimal } from '@kb3d/models/classes/handlers';
import { getByDynamicId } from '@kb3d/models/classes/kb3d-manager';
import { Kb3dObject } from '@kb3d/models/classes/kb3d-object';
import { isSpaceNode } from '@kb3d/models/classes/meshes/mesh-utilities';
import { SpaceNode } from '@kb3d/models/classes/meshes/space-node';
import { KbQuaternion } from '@kb3d/models/classes/primitives/kb-quaternion';
import { KbVector } from '@kb3d/models/classes/primitives/kb-vector';
import { Token } from '@kb3d/models/classes/token';
import { ITrackedClass } from '@kb3d/models/interfaces/tracked-class';
import { mateConnectors } from '@kb3d/view/helpers/mate-helper';
import { QuadTree } from '@kb3d/view/helpers/quadtree';
import { IScreenPosition, toScreen } from '@kb3d/view/helpers/scene-helper';
import { KbViewer } from '@kb3d/view/kb-viewer';
import { ConnectorRenderer, getNodeChain } from '@kb3d/view/render';

interface IPotential {
    otherMesh: TransformNode;
    proximity: number;
    distanceToCamera?: number;
}

interface IPotentialConnector extends IPotential {
    dragConnector: Connector;
    dragConnectorMesh: TransformNode;
}

interface IMeshScreenPosition {
    mesh: TransformNode;
    pos: IScreenPosition;
}

interface IConnectionMesh extends IConnection {
    dragConnectorMesh: TransformNode;
    otherConnectorMesh: TransformNode;
}

type MatedObject = SpaceNode | SketchControlPoint;

const CLICK_THRESHOLD = 8;

export class Dragger {
    constructor(private viewer: KbViewer, private scene: Scene) {}

    private _dragNode: SpaceNode;
    private _dragNodeStartPosition: KbVector;
    private _dragNodeInverseTransform: Matrix;
    private _dragMesh: TransformNode;
    private _draggable: IDraggable;
    private _pointerObserver: Nullable<Observer<PointerInfo>>;
    private _dragPlane: Plane;
    private _dragSurfaces: string[]; //array of dynamic id's of the drag surfaces if mode is "surface"
    private _startMouseIntersect?: Vector3;
    private _onComplete: () => void;
    private _externalConnectors = new Map<Connector, TransformNode>(); //connectors out in the scene that are not part of what is being dragged
    private _dragConnectors = new Map<Connector, TransformNode>(); //connectors that are children of what is being dragged
    private _dragConnectorsByDynamicId = new Map<string, Connector>(); //the drag connectors stored by their dynamic id
    private _dropPreviewConnectors = new Map<Connector, TransformNode>(); //the drop preview is a clone of the dragNode, this tracks the connector meshes in the drop preview
    private _oct: Octree<TransformNode>; //the octree holding connectors for fast retrieval
    private _quadTree: QuadTree<IMeshScreenPosition>;
    private _dropPreview: TransformNode;
    private _activeConnection: IConnectionMesh | undefined;
    private _connectorRenderer: ConnectorRenderer;
    private _dragFriends = new Set<MatedObject>();
    private _dragFriendMeshes = new Array<TransformNode>();
    private _dragFriendConnectors = new Set<Connector>();
    private _matedConnectors = new Set<Connector>();

    public startDrag(pointerArgs: IKb3dPointerArgs, draggable: IDraggable, onComplete: () => void) {
        let totalMoved = 0;
        let lastX: number;
        let lastY: number;

        this._pointerObserver = this.scene.onPointerObservable.add(p => {
            switch (p.type) {
                case PointerEventTypes.POINTERMOVE: {
                    let movX = 0;
                    let movY = 0;
                    if (typeof lastX === 'undefined' || typeof lastY === 'undefined') {
                        lastX = p.event.offsetX;
                        lastY = p.event.offsetY;
                    } else {
                        movX = Math.abs(lastX - p.event.offsetX);
                        movY = Math.abs(lastY - p.event.offsetY);
                    }
                    totalMoved += Math.sqrt(movX * movX + movY * movY);

                    if (totalMoved > CLICK_THRESHOLD) {
                        this.startDragCommit(pointerArgs, draggable, onComplete);
                        this.dragging(p);
                    }

                    break;
                }
                case PointerEventTypes.POINTERUP: {
                    if (totalMoved > CLICK_THRESHOLD) {
                        this.dropped(p);
                    }
                    this.scene.onPointerObservable.remove(this._pointerObserver);
                    this._pointerObserver = undefined as any;
                    break;
                }
            }
        });

        this.scene.activeCamera?.detachControl();
    }

    public startDragCommit(pointerArgs: IKb3dPointerArgs, draggable: IDraggable, onComplete: () => void) {
        if (!this._draggable) {
            this._connectorRenderer = this.viewer.rendererManager.connector;
            this._dragNode = pointerArgs.sourceNode!;
            this._dragNodeStartPosition = this._dragNode.position.clone();
            this._dragMesh = this.viewer.rendererManager.mesh.get3d(this._dragNode) as TransformNode;
            if (this._dragMesh.parent) {
                this._dragNodeInverseTransform = this._dragMesh.parent
                    .computeWorldMatrix(true)
                    .clone()
                    .setTranslation(Vector3.Zero())
                    .invert();
            }
            this._draggable = draggable;
            if (this._draggable.connectionMode == null) this._draggable.connectionMode = eConnectionMode.none;
            if (this._draggable.connectionType == null) this._draggable.connectionType = eMateType.fastened;
            if (this._draggable.connectRadius == null) {
                if (this._draggable.connectionMode == eConnectionMode.screen) {
                    this._draggable.connectRadius = 5; //5 pixels
                } else {
                    this._draggable.connectRadius = 0.3; //3d units
                }
            }
            if (this._draggable.dragOpacity == null) this._draggable.dragOpacity = 0.7;

            this._onComplete = onComplete;

            this.setupDragSurface(pointerArgs);

            if (this._draggable.dragStart) {
                this._draggable.dragStart(
                    this.getDragArgs(pointerArgs, this._dragNodeStartPosition, this._dragMesh.rotationQuaternion)
                );
            }

            this.setupDragFriends();
            this.setupConnectors();
            this.setOpacity(this._draggable.dragOpacity!, this._dragMesh, ...this._dragFriendMeshes);
        }
    }

    protected dragging(p: PointerInfo) {
        // this._dragNode._dragging = true;

        const newConnection = this.findConnection();
        //let connectionChanged = !this.connectionsAreEqual(newConnection, this._activeConnection);
        this._activeConnection = newConnection;

        if (this._activeConnection) {
            //if (connectionChanged) { //don't bother processing the connection if it's the same
            this.showDropPreview(this._activeConnection);
            //}
        } else {
            this.hideDropPreview();
        }
        this.processDragPosition(p, this._draggable.dragging);
    }

    protected applyDragResultToNode(node: SpaceNode, dragArgs: IDragArgs, applyEuler: boolean) {
        node.position = dragArgs.newPos;
        // If euler rotation was changed, apply the changed euler rotation. If not, then apply the quaternion rotation regardless of whether it was changed.
        if (applyEuler) {
            node.eulerRotation = dragArgs.newRotationEuler;
            const newKbQuat = KbQuaternion.FromYawPitchRoll(dragArgs.newRotationEuler);
            dragArgs.newRotationAngle = newKbQuat.getAngle();
            dragArgs.newRotationAxis = newKbQuat.getAxis();
            node.rotationAxis = dragArgs.newRotationAxis;
            node.rotationAngle = dragArgs.newRotationAngle;
        } else {
            node.rotationAxis = dragArgs.newRotationAxis;
            node.rotationAngle = dragArgs.newRotationAngle;
            node.eulerRotation = KbQuaternion.FromRotationAxis(
                dragArgs.newRotationAxis,
                dragArgs.newRotationAngle
            ).toEuler();
        }
    }

    protected dropped(p: PointerInfo) {
        this._dragNode._primeMover = false;

        const complete = new Promise<void>(resolve => {
            if (this._activeConnection) {
                this._activeConnection.otherConnector._parent!._primeMover = true;
                this.connect(this._activeConnection, this._dragMesh);
                // let newRotation = KbVector.FromVec3(this._dragMesh.rotation);

                const dragArgs: IDragArgs & Required<Pick<IDragArgs, 'connection'>> = {
                    ...this.getDragArgs(p, this._dragMesh.position, this._dragMesh.rotationQuaternion),
                    connection: this._activeConnection,
                };
                if (this._draggable.withMates) {
                    dragArgs.mate = this.viewer.sceneNode._manager!.create(Token.MateNode, {
                        connector1: this.viewer.sceneNode.getNestedPathTo(dragArgs.connection.otherConnector),
                        connector2: this.viewer.sceneNode.getNestedPathTo(dragArgs.connection.dragConnector),
                        mateType: this._draggable.connectionType,
                    });
                }
                const previousEuler = dragArgs.newRotationEuler.clone();

                this.applyDragResultToNode(this._dragNode, dragArgs, !previousEuler.equal(dragArgs.newRotationEuler));
                if (dragArgs.mate) {
                    this.viewer.sceneNode.mates.push(dragArgs.mate);
                }

                this.viewer.forceAndWaitForNextRender().then(() => {
                    if (this._draggable.dropped) {
                        this._draggable.dropped(dragArgs);

                        if (!dragArgs.allow) {
                            console.warn(
                                'Cannot disallow dropping when using connectors, use canConnect handler instead'
                            );
                        }
                    }
                    resolve();
                });
            } else {
                this.processDragPosition(p, this._draggable.dragging);
                this.processDragPosition(p, this._draggable.dropped);
                resolve();
            }
        });

        complete.then(() => {
            this.toggleAllConnectors(false);
            this.resetOpacity(this._dragMesh, ...this._dragFriendMeshes);
            this.scene.activeCamera?.attachControl(this.viewer.canvas!);
            if (this._onComplete) this._onComplete();

            this.disposeDropPreview();
            this._onComplete = undefined as any;
            this._dragNode = undefined as any;
            this._dragMesh = undefined as any;
            this._draggable = undefined as any;
            this._dragPlane = undefined as any;
            this._dragSurfaces = undefined as any;
            this._onComplete = undefined as any;
            if (this._externalConnectors) this._externalConnectors.clear();
            if (this._dragConnectors) this._dragConnectors.clear();
            if (this._dragConnectorsByDynamicId) this._dragConnectorsByDynamicId.clear();
            if (this._dropPreviewConnectors) this._dropPreviewConnectors.clear();
            this._oct = undefined as any;
            this._quadTree = undefined as any;
            if (this._quadTree) this._quadTree.items;
            this._dropPreview = undefined as any;
            this._activeConnection = undefined as any;
            this._connectorRenderer = undefined as any;
            if (this._dragFriends) this._dragFriends.clear();
            if (this._dragFriendMeshes) this._dragFriendMeshes.clear();
            if (this._dragFriendConnectors) this._dragFriendConnectors.clear();
            if (this._matedConnectors) this._matedConnectors.clear();
        });
    }

    protected processDragPosition(p: PointerInfo, event?: (a: IDragArgs) => void) {
        const newMouseIntersect = this.intersectPointerToDragSurface(p);
        if (newMouseIntersect) {
            let newPos: KbVector;
            if (this._draggable.disableOffset || this._draggable.mode == eDragMode.surface) {
                newPos = KbVector.FromVec3(newMouseIntersect);
            } else {
                if (this._startMouseIntersect) {
                    const delta = newMouseIntersect.subtract(this._startMouseIntersect);
                    if (this._dragNodeInverseTransform) {
                        Vector3.TransformCoordinatesToRef(delta, this._dragNodeInverseTransform, delta);
                    }
                    newPos = this._dragNodeStartPosition.add(delta);
                } else {
                    newPos = this._dragNodeStartPosition;
                    this._startMouseIntersect = newMouseIntersect;
                }
            }

            const dragArgs = this.getDragArgs(p, newPos, this._dragMesh.rotationQuaternion);
            const previousEuler = dragArgs.newRotationEuler.clone();
            if (event) {
                event(dragArgs);
            }
            if (dragArgs.allow) {
                this.applyDragResultToNode(this._dragNode, dragArgs, !previousEuler.equal(dragArgs.newRotationEuler));
            }
            return dragArgs;
        }
        return undefined;
    }

    protected setupDragSurface(p: IKb3dPointerArgs) {
        const d = this._draggable;
        const manager = this._dragNode._manager!;
        if (d.mode == eDragMode.plane) {
            // d.planeOrigin = p.pickedPoint!; // d.planeOrigin || this._dragNode.position;

            /** intersection plane is defined by the pickedPoint and the plane normal */
            d.planeNormal = d.planeNormal || new KbVector(0, 1, 0);
            this._dragPlane = Plane.FromPositionAndNormal(p.pickInfo!.pickedPoint!, d.planeNormal!.toVec3());
        } else if (d.mode == eDragMode.free) {
            const cameraDirection = this.scene.activeCamera!.position.subtract(this._dragMesh.getAbsolutePosition());
            this._dragPlane = Plane.FromPositionAndNormal(this._dragNode.position.toVec3(), cameraDirection);
        } else if (d.mode == eDragMode.surface) {
            const surfaceIds = Array.isArray(d.surfaceNodes) ? d.surfaceNodes : [d.surfaceNodes];
            this._dragSurfaces =
                (surfaceIds.map(sn => sn && manager.getById(sn)?._dynamicId).filter(sn => sn != null) as string[]) ||
                [];
        }
    }

    protected intersectPointerToDragSurface(p: PointerInfo) {
        return this.intersectDragSurface(p.pickInfo!.ray!);
    }

    protected intersectDragSurface(ray: Ray): Vector3 | undefined {
        switch (this._draggable.mode) {
            case eDragMode.plane:
            case eDragMode.free: {
                const distance = ray.intersectsPlane(this._dragPlane);
                if (distance != null) {
                    return ray.direction.scale(distance).add(ray.origin);
                }
                break;
            }
            case eDragMode.surface: {
                const surfacePick = this.scene.pickWithRay(ray, mesh => {
                    return this._dragSurfaces.some(sn => mesh.id == sn);
                });
                if (surfacePick && surfacePick.pickedPoint) {
                    return surfacePick.pickedPoint;
                }
                break;
            }
        }

        return undefined;
    }

    protected getDragArgs(
        p: PointerInfoMinimal,
        newPos: KbVector | Vector3,
        newRotation?: Nullable<Quaternion>
    ): IDragArgs {
        const newKbRotation = KbQuaternion.FromQuat(newRotation || Quaternion.Identity());
        const faceUV = this.getDragFaceUV(p);

        return {
            dragNode: this._dragNode,
            startPos: this._dragNodeStartPosition,
            allow: true,
            u: faceUV?.x ?? 0.5,
            v: faceUV?.y ?? 0.5,
            newPos: newPos instanceof Vector3 ? KbVector.FromVec3(newPos) : newPos,
            newRotationAxis: newKbRotation.getAxis(),
            newRotationAngle: newKbRotation.getAngle(),
            newRotationEuler: newKbRotation.toEuler(),
        };
    }

    protected getDragFaceUV(p: PointerInfoMinimal) {
        if (this._draggable?.mode == eDragMode.surface) {
            // Cached from the surface-specific pick in intersectDragSurface (same pick used for position).
            // On drag start (before first surface pick), fall back to the initial click pickInfo.
            const tc = p.pickInfo?.getTextureCoordinates();
            return (tc && isFinite(tc.x) && isFinite(tc.y)) ? { x: tc.x, y: tc.y } : undefined;
        }
        // For plane/free mode, the surface UV under the pointer is computed from the pickInfo.
        const tc = p.pickInfo?.hit ? p.pickInfo.getTextureCoordinates() : null;
        return (tc && isFinite(tc.x) && isFinite(tc.y)) ? { x: tc.x, y: tc.y } : undefined;
    }

    protected findConnection(): IConnectionMesh | undefined {
        if (this._draggable.connectionMode != eConnectionMode.none) {
            const potentialConnectors = new Array<IPotentialConnector>();

            for (const [dragConnector, dragConnectorMesh] of this._dragConnectors) {
                const dragConPos = dragConnectorMesh.getAbsolutePosition();
                for (const p of this.intersectOctree(dragConPos)) {
                    potentialConnectors.push({
                        ...p,
                        dragConnector,
                        dragConnectorMesh,
                    });
                }
            }

            if (this._draggable.connectionMode == eConnectionMode.world) {
                potentialConnectors.sortBy(p => p.proximity);
            } else {
                potentialConnectors.sortBy(p => p.distanceToCamera);
            }

            for (const p of potentialConnectors) {
                const otherConnector = getByDynamicId<Connector>(p.otherMesh.id)!;
                const a = {
                    dragConnector: p.dragConnector,
                    otherConnector: otherConnector,
                    dragNode: p.dragConnector._parent,
                    otherNode: otherConnector._parent,
                    allow: true,

                    // Private properties below, don't expose to Snap
                    dragConnectorMesh: p.dragConnectorMesh,
                    otherConnectorMesh: p.otherMesh,
                };
                if (this._draggable.canConnect) {
                    this._draggable.canConnect(a);
                }
                if (a.allow) {
                    return a;
                }
            }
        }
    }

    protected connectionsAreEqual(c1?: IConnection, c2?: IConnection) {
        return (
            (!c1 && !c2) || (c1 && c2 && c1.dragConnector == c2.dragConnector && c1.otherConnector == c2.otherConnector)
        );
    }

    protected setupConnectors() {
        if (this._draggable.connectionMode != eConnectionMode.none) {
            const externalConnectorMeshes = new Set<TransformNode>();
            for (const c of this.viewer.sceneNode.getAllConnectors()) {
                if (
                    !this._dragFriendConnectors.has(c) &&
                    (!this._draggable.withMates || !this._matedConnectors.has(c)) &&
                    this.parentsEnabled(c)
                ) {
                    let mesh = this.getConnectorMesh(c);
                    if (!mesh) {
                        this._connectorRenderer.update3d(c as ITrackedClass<Connector>);
                        mesh = this.getConnectorMesh(c);
                    }
                    externalConnectorMeshes.add(mesh);
                    this._externalConnectors.set(c, mesh);
                }
            }
            const dragConnectors = this._dragNode
                .filterNodes<SpaceNode>(n => isSpaceNode(n) && n.visible, true, false)
                .selectMany(n => n.connectors);

            for (const c of dragConnectors) {
                //remove the drag connectors from the outside connectors
                if (this._externalConnectors.has(c)) {
                    const mesh = this._externalConnectors.get(c)!;
                    this._dragConnectors.set(c, mesh);
                    this._dragConnectorsByDynamicId.set(c._dynamicId, c);
                    this._externalConnectors.delete(c);
                    externalConnectorMeshes.delete(mesh);
                }
            }

            this.toggleAllConnectors(true);

            if (this._draggable.connectionMode == eConnectionMode.world) {
                //create an octree for holding the connectors
                this._oct = new Octree(Octree.CreationFuncForMeshes, 64, 2);

                //get the bounding extents of all the external connectors to contain the octree volume
                const boundingVectors = this.scene.getWorldExtends(m => externalConnectorMeshes.has(m));

                //update the octree with the connector meshes
                this._oct.update(boundingVectors!.min, boundingVectors!.max, [...externalConnectorMeshes]);
            } else {
                //screen mode
                const extConnectorPositions = new Array<{ mesh: TransformNode; pos: IScreenPosition }>();
                externalConnectorMeshes.forEach(m => {
                    extConnectorPositions.push({
                        mesh: m,
                        pos: toScreen(m.getAbsolutePosition(), this.scene.activeCamera!),
                    });
                });

                const engine = this.scene.getEngine();
                this._quadTree = new QuadTree({
                    items: extConnectorPositions,
                    xSelector: d => d.pos.x,
                    ySelector: d => d.pos.y,
                    capacity: 4,
                    xmin: 0,
                    xmax: engine.getRenderWidth(),
                    ymin: 0,
                    ymax: engine.getRenderHeight(),
                });
            }
        }
    }

    protected parentsEnabled(item: Kb3dObject) {
        while (item._parent && item._parent instanceof SpaceNode) {
            if (!item._parent.visible) {
                return false;
            }
            item = item._parent;
        }
        return true;
    }

    protected setupDragFriends() {
        const activeMates = this.viewer.getActiveMates();
        for (const mate of activeMates) {
            const c1 = mate.getC1();
            if (c1 && isConnector(c1)) this._matedConnectors.add(c1);
            const c2 = mate.getC2();
            if (c2 && isConnector(c2)) this._matedConnectors.add(c2);
        }

        this._dragFriends = getNodeChain(this._dragNode as MatedObject, activeMates);
        this._dragFriends.forEach(friend => {
            if (!(friend instanceof SpaceNode)) {
                this._dragFriends.delete(friend);
            }
        });

        for (const friendNode of this._dragFriends) {
            if (friendNode instanceof SpaceNode) {
                friendNode._primeMover = false;

                const friendMesh = this.viewer.rendererManager.mesh.get3d(friendNode);
                if (friendMesh) {
                    this._dragFriendMeshes.push(friendMesh);
                }

                const connectors = friendNode.filterByType<Connector>(Token.Connector);
                for (const c of connectors) this._dragFriendConnectors.add(c);
            }
        }
        this._dragNode._primeMover = true;
    }

    protected getConnectorMesh(c: Connector) {
        return this._connectorRenderer.get3d(c)!;
    }

    protected toggleAllConnectors(show: boolean) {
        if (this._draggable.showConnectors) {
            for (const [c] of this._externalConnectors) {
                c._visible = show;
            }
            for (const [c] of this._dragConnectors) {
                c._visible = show;
            }
        }
    }

    protected intersectOctree(v: Vector3): IPotential[] {
        let potentials = new Array<IPotential>();
        if (this._draggable.connectionMode == eConnectionMode.world) {
            const bpotentials = this._oct.intersects(v, this._draggable.connectRadius!, false);
            bpotentials.forEach(p => {
                const proximity = p.getAbsolutePosition().subtract(v).length();
                if (proximity < this._draggable.connectRadius!) {
                    potentials.push({ otherMesh: p, proximity });
                }
            });
            //potentials.sortBy(p => p.proximity);
        } else {
            //screen mode
            const screenPos = toScreen(v, this.scene.activeCamera!);

            potentials = this._quadTree.find(screenPos, this._draggable.connectRadius!).map(p => {
                return {
                    otherMesh: p.item.mesh,
                    proximity: p.proximity,
                    distanceToCamera: Vector3.Distance(
                        this.scene.activeCamera!.position,
                        p.item.mesh.getAbsolutePosition()
                    ),
                };
            });
            //potentials.sortBy(i => i.distanceToCamera);

            // let ray = this.scene.createPickingRay(screenPos.x, screenPos.y, Matrix.Identity(), this.scene.activeCamera);
            // let bpotentials = this._oct.intersectsRay(ray);
            // bpotentials.forEach(p => {
            //     let potentialScreenPos = toScreen(p.getAbsolutePosition(), this.scene.activeCamera!);
            //     let screenProximity = Math.sqrt(Math.pow(screenPos.x - potentialScreenPos.x, 2) + Math.pow(screenPos.y - potentialScreenPos.y, 2));
            //     let realProximity = p.getAbsolutePosition().subtract(v).length();
            //     console.log({ screenProximity, realProximity });
            //     if (screenProximity < this._draggable.connectRadius!) {
            //         potentials.push({ mesh: p, proximity: screenProximity });
            //     }
            // });
            // potentials.sortBy(p => p.proximity);
        }
        return potentials;
    }

    protected showDropPreview(c: IConnectionMesh) {
        if (!this._dropPreview) {
            /**
             * for the drop preview, we get the drag node and any other nodes that are connected to it through mates
             * and clone them, put them in a transformNode, and then that transform node becomes the drop preview
             */
            this._dropPreview = new TransformNode('_droppreview', this.viewer.scene);
            this._dropPreview.position = Vector3.Zero();
            this._dropPreview.rotationQuaternion = Quaternion.Identity();
            this._dropPreview.computeWorldMatrix(true);
            const dragMeshTransform = this._dragMesh.computeWorldMatrix(true).getRotationMatrix();
            dragMeshTransform.invert();

            for (const friendMesh of [this._dragMesh, ...this._dragFriendMeshes]) {
                createDragPreview(friendMesh, this._dropPreview, this);
            }
            this._dropPreview.setPreTransformMatrix(dragMeshTransform);
        } else {
            //line drop preview back up with the source models as they might have rotated
            if (this._draggable.connectionType != eMateType.fastened) {
                this.connect(
                    {
                        dragConnector: c.dragConnector,
                        otherConnector: c.dragConnector,
                        otherConnectorMesh: c.dragConnectorMesh,
                        dragConnectorMesh: this._dropPreviewConnectors.get(c.dragConnector)!, //pass in the drop preview connector mesh
                    },
                    this._dropPreview,
                    eMateType.fastened,
                    true
                );
            }
        }

        this.connect(
            {
                ...c,
                dragConnectorMesh: this._dropPreviewConnectors.get(c.dragConnector)!, //pass in the drop preview connector mesh
            },
            this._dropPreview
        );

        this._dropPreview.setEnabled(true);
        //hide the drag node
        this._dragMesh.setEnabled(false);
        for (const friend of this._dragFriendMeshes) friend.setEnabled(false);

        function createDragPreview(mesh: TransformNode, parent: TransformNode, self: Dragger) {
            const clonedMeshGroup = mesh.clone(mesh.id + '_dropPreview', mesh.parent, true);
            if (clonedMeshGroup) {
                clonedMeshGroup.setPivotMatrix(Matrix.Identity());

                // Cloning does not copy the pretransform matrix over so we have to do that manually
                // Otherwise the connector meshes scaling does not get applied to the preview meshes
                clonedMeshGroup.setEnabled(mesh.isEnabled());
                if (c instanceof Mesh && mesh instanceof Mesh) {
                    c.setPreTransformMatrix(mesh.getPivotMatrix());
                }

                clonedMeshGroup.setParent(parent); // setting the parent with setParent will make the node stay in the same absolute position.
                const children = mesh.getChildren(child => child instanceof TransformNode, true);
                children.forEach(child => {
                    createDragPreview(child as TransformNode, clonedMeshGroup, self);
                });

                clonedMeshGroup.metadata = {}; // babylon metadata refers to same instance of object after cloning so we clear it out
                const kbnode = getByDynamicId(mesh.id);
                if (kbnode && kbnode instanceof Connector) {
                    const connector = kbnode as Connector;
                    self._dropPreviewConnectors.set(connector, clonedMeshGroup as Mesh);
                    clonedMeshGroup.setEnabled(connector._visible); //connector.enabled && !!this._draggable.showConnectors);
                } else {
                    self.setOpacity(self._draggable.dragOpacity!, clonedMeshGroup);
                }
            }
        }
    }

    protected connect(
        c: IConnectionMesh,
        followerMesh: TransformNode,
        mateType = this._draggable.connectionType,
        flip = false
    ) {
        this.viewer.pauseRenderWhile(() => {
            mateConnectors(
                {
                    mateType: mateType!,
                    flip: flip,
                    offsetX: 0,
                    offsetY: 0,
                    offsetZ: 0,
                },
                followerMesh,
                c.otherConnectorMesh,
                c.dragConnectorMesh
            );
        });
    }

    protected hideDropPreview() {
        if (this._dropPreview) this._dropPreview.setEnabled(false);
        this._dragMesh.setEnabled(true); //show the drag node again
        if (this._dragFriendMeshes) {
            for (const friend of this._dragFriendMeshes) {
                friend.setEnabled(getByDynamicId<SpaceNode>(friend.id)!.visible);
            }
        }
    }

    protected disposeDropPreview() {
        this.hideDropPreview();
        if (this._dropPreview) this._dropPreview.dispose();
        if (this._dragFriendMeshes) this._dragFriendMeshes.clear();
        if (this._dragFriends) this._dragFriends.clear();
    }

    protected getConnectorLocalPoint(connector: Connector) {
        if (connector.positionMode == eConnectorPositionMode.absolute) {
            return connector.position;
        } else {
            //TODO
        }
    }

    protected setOpacity(value: number, ...nodes: TransformNode[]) {
        if (this.shouldSetOpacity()) {
            for (const node of nodes) {
                for (const m of this.getAllMeshes(node, m => !(m instanceof InstancedMesh))) {
                    //set opacity of all non-connector meshes being dragged
                    if (!m.metadata) {
                        m.metadata = {};
                    }
                    m.metadata.originalVisibility = m.visibility;
                    m.visibility = value;
                }
            }
        }
    }

    protected resetOpacity(...nodes: TransformNode[]) {
        for (const node of nodes) {
            for (const m of this.getAllMeshes(node, m => !(m instanceof InstancedMesh))) {
                //set opacity of all non-connector meshes being dragged
                if (m.metadata && m.metadata.originalVisibility !== undefined) {
                    m.visibility = m.metadata.originalVisibility;
                    delete m.metadata.originalVisibility;
                }
            }
        }
    }

    protected shouldSetOpacity() {
        return this._draggable.connectionMode != eConnectionMode.none && this._draggable.showConnectors; // && this._dragConnectors.size ;
    }

    /**get's all child meshes and includes the given node if it's a mesh */
    protected getAllMeshes(node: TransformNode, predicate?: (node: Node) => boolean) {
        const meshes = new Set<AbstractMesh>(node.getChildMeshes(false, predicate));
        if (node instanceof AbstractMesh) meshes.add(node);
        return meshes;
    }
}
